/// <amd-module name="Repos/PipelineStatus/Util/GitStatusState" />
import { GitStatus, GitStatusState } from "Repos/Common/Contracts/Git";
import { processGitStatusArtifactUris } from "Repos/Common/Util/Build";
import * as Resources from "Repos/PipelineStatus/Resources";
import { getTFSPageData } from "Tfs/Platform/Page";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { routeUrl } from "VSS/Platform/Location";
import { IStatusProps, Statuses } from "VSSUI/Status";

const releaseSummaryRoute = "ms.vss-releaseManagement-web.cd-release-progress-default-route";
const releaseManagementStatusGenre = "continuous-deployment/release";
const releaseManagementContextNameSeperator = ")^!!(!";
const releaseManagementTruncatedPostFix = "#$";
const releaseDefinitionNameMaxLength = 90;

export interface IGitStatusStateProps {
    statusClass: string;
    statusText: string;
    statusProps: IStatusProps;
}

interface IReleaseStatusContext {
    definitionName: string;
    rank: number;
}

export function getGitStatusStateProps(status: GitStatus): IGitStatusStateProps {
    const result: IGitStatusStateProps = {
        statusClass: "repos-pipeline-status-success",
        statusText: Resources.Succeeded,
        statusProps: Statuses.Success
    };
    switch (status.state) {
        case GitStatusState.Error:
            result.statusProps = Statuses.Failed;
            result.statusText = Resources.Error;
            result.statusClass = "repos-pipeline-status-failed";
            break;
        case GitStatusState.Failed:
            result.statusProps = Statuses.Failed;
            result.statusText = Resources.Failed;
            result.statusClass = "repos-pipeline-status-failed";
            break;
        case GitStatusState.Pending:
            result.statusProps = Statuses.Running;
            result.statusText = Resources.InProgress;
            result.statusClass = "repos-pipeline-status-pending";
            break;
        case GitStatusState.PartiallySucceeded:
            result.statusProps = Statuses.Warning;
            result.statusText = Resources.PartiallySucceeded;
            result.statusClass = "repos-pipeline-status-warning";
            break;
        default:
            result.statusProps = Statuses.Success;
            result.statusText = Resources.Succeeded;
            result.statusClass = "repos-pipeline-status-success";
    }

    return result;
}

export function getReleaseGitStatusesSortedByRank(statuses: GitStatus[]): GitStatus[] {
    return statuses.sort((status1: GitStatus, status2: GitStatus) => {
        const status1Context = parseReleaseContextName(status1.context.name);
        const status2Context = parseReleaseContextName(status2.context.name);

        if (!!status1Context && !!status2Context && !isNaN(status1Context.rank) && !isNaN(status2Context.rank)) {
            return status1Context.rank - status2Context.rank;
        }

        // This shouldn't be the case, but we will ignore this and push all these instances to the end
        return 1;
    });
}

export function getReleaseSummaryUrl(pageContext: IVssPageContext, releaseStatuses: GitStatus[]): string {
    const tfsPageData = getTFSPageData(pageContext);
    const projectName = tfsPageData && tfsPageData.project && tfsPageData.project.name;
    const projectId = tfsPageData && tfsPageData.project && tfsPageData.project.id;

    let isCrossProjectReleaseReferenced = false;
    if (projectId && projectId.length > 0) {
        isCrossProjectReleaseReferenced = !(releaseStatuses[0].targetUrl.includes(projectId));
    }

    if (!projectName || isCrossProjectReleaseReferenced) {
        return releaseStatuses[0].targetUrl;
    } else {
        const maxReleaseId = Math.max(
            ...releaseStatuses.map(s => {
                const maxId = s.targetUrl.match(/releaseId=([0-9]+)/);
                if (maxId) {
                    return parseInt(maxId[1], 10);
                }
                return -1;
            })
        );

        const routeValues: { [key: string]: string } = {};
        routeValues["releaseId"] = maxReleaseId.toString();
        routeValues["_a"] = "release-pipeline-progress";
        routeValues["project"] = projectName;

        return routeUrl(pageContext, releaseSummaryRoute, routeValues);
    }
}

export function processGitStatuses(
    statuses: GitStatus[],
    releaseStatuses: { [key: string]: GitStatus[] },
    nonReleaseStatuses: GitStatus[],
    pageContext: IVssPageContext
): void {
    const tfsPageData = getTFSPageData(pageContext);
    const projectName = tfsPageData && tfsPageData.project && tfsPageData.project.name;

    const convertedStatuses = projectName ? processGitStatusArtifactUris(pageContext, projectName, statuses) : statuses;

    convertedStatuses.forEach((status: GitStatus) => {
        if (status.context.genre === releaseManagementStatusGenre) {
            const context = parseReleaseContextName(status.context.name);
            if (!!context && !!context.definitionName) {
                if (!releaseStatuses.hasOwnProperty(context.definitionName)) {
                    releaseStatuses[context.definitionName] = [];
                }

                releaseStatuses[context.definitionName].push(status);
            } else {
                nonReleaseStatuses.push(status);
            }
        } else {
            nonReleaseStatuses.push(status);
        }
    });

    return;
}

function parseReleaseContextName(contextName: string): IReleaseStatusContext | undefined {
    if (!contextName) {
        return undefined;
    }
    let seperator: string = releaseManagementContextNameSeperator;
    if (contextName.indexOf(seperator) < 0) {
        seperator = "/";
    }

    const parts = contextName.split(seperator);

    // Environment name was a part of the context previously. It has been removed because it wasn't required
    // Old statuses will have 3 parts. We need to handler both cases
    // "/" was the old seperator. So if the context name contains rd name and rank, the seperator shouldn't be "/"
    if (parts.length !== 3 && (parts.length !== 2 || seperator === "/")) {
        return undefined;
    }

    const definitionName =
        parts[0].lastIndexOf(releaseManagementTruncatedPostFix) === releaseDefinitionNameMaxLength - releaseManagementTruncatedPostFix.length
            ? format("{0}...", parts[0].substring(0, releaseDefinitionNameMaxLength - releaseManagementTruncatedPostFix.length))
            : parts[0];

    let rank = 0;
    if (parts.length === 3) {
        rank = parseInt(parts[2], 10);
    } else if (parts.length === 2) {
        rank = parseInt(parts[1], 10);
    }

    return {
        definitionName: definitionName,
        rank: rank
    };
}
